import { Pencil, Trash2 } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { cestinoAPI } from '../services/api';
import '../styles/Cestino.css';

/**
 * Cestino — schermata riservata agli admin.
 *
 * Mostra le cancellazioni non ancora ripristinate e permette di
 * riportare indietro i dati. L'eliminazione definitiva richiede di
 * digitare la parola di conferma: una rotta irreversibile non deve
 * poter essere attivata per distrazione.
 */

const messaggioErrore = (e, ripiego) =>
    e?.message || e?.response?.data?.message || ripiego;

const dataOra = (v) =>
    v ? new Date(v).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const etichettaEntita = {
    paziente: 'Paziente',
    visita: 'Visita',
    documento: 'Documento',
    anamnesi: 'Anamnesi',
};

const Cestino = () => {
    const [voci, setVoci] = useState([]);
    const [caricamento, setCaricamento] = useState(true);
    const [errore, setErrore] = useState(null);
    const [avviso, setAvviso] = useState(null);
    const [inCorso, setInCorso] = useState(null);

    // Eliminazione definitiva
    const [daEliminare, setDaEliminare] = useState(null);
    const [conferma, setConferma] = useState('');

    const carica = async () => {
        try {
            setCaricamento(true);
            const r = await cestinoAPI.getAll();
            setVoci(r.data.data.cancellazioni || []);
        } catch (e) {
            setErrore(messaggioErrore(e, 'Impossibile caricare il cestino'));
        } finally {
            setCaricamento(false);
        }
    };

    useEffect(() => { carica(); }, []);

    const ripristina = async (v) => {
        if (!window.confirm(`Ripristinare "${v.descrizione}"?\n\nTornerà visibile con tutti i dati collegati.`)) return;
        setErrore(null);
        setAvviso(null);
        try {
            setInCorso(v.id);
            await cestinoAPI.ripristina(v.id);
            setAvviso(`"${v.descrizione}" è stato ripristinato.`);
            carica();
        } catch (e) {
            setErrore(messaggioErrore(e, 'Errore nel ripristino'));
        } finally {
            setInCorso(null);
        }
    };

    const confermaEliminazione = async () => {
        if (conferma !== 'ELIMINA') return;
        setErrore(null);
        setAvviso(null);
        try {
            setInCorso(daEliminare.id);
            await cestinoAPI.elimina(daEliminare.id, conferma);
            setAvviso('Eliminazione definitiva completata.');
            setDaEliminare(null);
            setConferma('');
            carica();
        } catch (e) {
            setErrore(messaggioErrore(e, 'Errore nell\'eliminazione'));
        } finally {
            setInCorso(null);
        }
    };

    const riepilogoTesto = (r) => {
        if (!r) return null;
        const parti = Object.entries(r)
            .filter(([, n]) => Number(n) > 0)
            .map(([k, n]) => `${n} ${k.replace('_', ' ')}`);
        return parti.length ? parti.join(', ') : 'nessun dato collegato';
    };

    return (
        <div className="ce-wrap">
            <div className="ce-testa">
                <div>
                    <h1>Cestino</h1>
                    <p className="ce-sottotitolo">
                        Dati cancellati e ancora recuperabili. Il ripristino riporta indietro
                        il record e tutto ciò che era stato nascosto insieme a lui.
                    </p>
                </div>
                <button className="ce-btn-secondario" onClick={carica} disabled={caricamento}>
                    Aggiorna
                </button>
            </div>

            {errore && <div className="ce-errore">{errore}</div>}
            {avviso && <div className="ce-avviso">{avviso}</div>}

            {caricamento ? (
                <div className="ce-stato">Caricamento…</div>
            ) : voci.length === 0 ? (
                <div className="ce-vuoto">
                    <div className="ce-vuoto-icona"><Trash2 size={38} strokeWidth={1.2} aria-hidden="true" /></div>
                    <p>Il cestino è vuoto.</p>
                </div>
            ) : (
                <div className="rm-scorre">
                <table className="ce-tabella">
                    <thead>
                        <tr>
                            <th>Tipo</th>
                            <th>Descrizione</th>
                            <th>Dati collegati</th>
                            <th>Cancellato da</th>
                            <th>Quando</th>
                            <th>Azioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {voci.map((v) => (
                            <tr key={v.id}>
                                <td><span className="ce-tipo">{etichettaEntita[v.entita] || v.entita}</span></td>
                                <td>
                                    <strong>{v.descrizione || '—'}</strong>
                                    {v.codice_fiscale && <div className="ce-cf">{v.codice_fiscale}</div>}
                                    {v.motivo && <div className="ce-motivo">{v.motivo}</div>}
                                </td>
                                <td className="ce-riepilogo">{riepilogoTesto(v.riepilogo) || '—'}</td>
                                <td>
                                    {v.utente_cognome
                                        ? `${v.utente_cognome} ${v.utente_nome || ''}`.trim()
                                        : <span className="ce-ignoto">non registrato</span>}
                                </td>
                                <td className="rm-intero">{dataOra(v.created_at)}</td>
                                <td>
                                    <div className="ce-azioni">
                                        <button
                                            className="ce-link"
                                            onClick={() => ripristina(v)}
                                            disabled={inCorso === v.id}
                                        >
                                            Ripristina
                                        </button>
                                        {v.entita === 'paziente' && (
                                            <button
                                                className="ce-link ce-link-pericolo"
                                                onClick={() => { setDaEliminare(v); setConferma(''); setErrore(null); }}
                                                disabled={inCorso === v.id}
                                            >
                                                Elimina
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>
            )}

            {daEliminare && (
                <div className="ce-overlay" onClick={() => setDaEliminare(null)}>
                    <div className="ce-modale" onClick={(e) => e.stopPropagation()}>
                        <h2>Eliminazione definitiva</h2>
                        {/* IL TESTO DICE QUELLO CHE SUCCEDE, NON DI PIU'
                            (corretto il 23 settembre 2026). Diceva «Non sarà
                            più recuperabile in alcun modo», ed era falso: il
                            registro delle modifiche conserva `record_prima`
                            per sempre, di proposito - `pulisciRegistri.js`
                            dichiara in testa che modifiche_log non si tocca.
                            In produzione ci sono 47 righe DELETE di pazienti
                            che non esistono piu', tutte con codice fiscale,
                            nome, indirizzo, telefono ed email, piu' le righe
                            cancellate a cascata: documenti, consensi,
                            risposte ai questionari, firme grafiche.
                            La conservazione puo' essere giusta per gli
                            obblighi sulla cartella. Il difetto era che
                            l'operazione prometteva il contrario, e un
                            amministratore poteva dirlo in buona fede a un
                            paziente che aveva chiesto la cancellazione. */}
                        <div className="ce-allarme">
                            Stai per rimuovere <strong>{daEliminare.descrizione}</strong> e
                            tutti i dati collegati: visite, documenti firmati, foto, anamnesi.
                            I file verranno cancellati dal disco e la scheda non sarà più
                            recuperabile dall'applicazione.
                        </div>
                        <p className="ce-nota">
                            <strong>Quello che resta.</strong> Nel registro delle modifiche
                            rimangono la scheda e le righe cancellate così com'erano — anagrafica
                            completa, documenti, consensi, risposte ai questionari, firme — e
                            rimangono anche nelle copie di sicurezza notturne. È voluto: il
                            registro è la memoria di chi ha fatto cosa, e non si cancella.
                            <strong> Quindi non si può dire a un paziente che i suoi dati non
                            esistono più.</strong>
                        </p>
                        <p className="ce-nota">
                            Ricorda che sui dati sanitari esistono obblighi di conservazione che
                            possono prevalere sulla richiesta di cancellazione del paziente.
                        </p>
                        <div className="ce-campo">
                            <label htmlFor="conferma">
                                Per procedere, scrivi <code>ELIMINA</code>
                            </label>
                            <input
                                id="conferma"
                                type="text"
                                value={conferma}
                                onChange={(e) => setConferma(e.target.value)}
                                autoComplete="off"
                                placeholder="ELIMINA"
                            />
                        </div>
                        {errore && <div className="ce-errore">{errore}</div>}
                        <div className="ce-modale-azioni">
                            <button className="ce-btn-secondario" onClick={() => setDaEliminare(null)}>
                                Annulla
                            </button>
                            <button
                                className="ce-btn-pericolo"
                                onClick={confermaEliminazione}
                                disabled={conferma !== 'ELIMINA' || inCorso === daEliminare.id}
                            >
                                {inCorso === daEliminare.id ? 'Eliminazione…' : 'Elimina per sempre'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Cestino;
